<template>
  <div>
    <div
      class="dropdown"
      :class="{ 'dropdown--open': isOpen }"
      @focusin="isOpen = true"
      @focusout="handleFocusOut"
      @mouseover="isOpen = true"
      @mouseleave="isOpen = false"
      @keydown.esc="isOpen = false"
    >
      <button
        :id="mainButtonId"
        class="main-button"
        :title="t('accessibility-settings')"
        :aria-expanded="isOpen"
        :aria-haspopup="true"
        :aria-controls="dropdownContentId"
        @click="isOpen = !isOpen"
      >
        <A11yPersonIcon class="icon" />
      </button>
      <Transition name="fade">
        <div
          v-show="isOpen"
          :id="dropdownContentId"
          class="content"
          role="menu"
          :aria-labelledby="mainButtonId"
        >
          <NavigationContrastSwitcher
            class="element-button"
            icon-class="nested-icon"
            role="menuitem"
          />
          <NavigationFontSwitcher
            class="element-button"
            variant="singleButton"
            role="menuitem"
          />
        </div>
      </Transition>
    </div>
    <Transition name="fade">
      <div v-if="isOpen" class="backdrop" />
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import A11yPersonIcon from '@/assets/icons/a11y-person.svg?component'

const isOpen = ref(false)

const { t } = useI18n()

const mainButtonId = useId()
const dropdownContentId = useId()

const handleFocusOut = (event: FocusEvent) => {
  if (
    event.relatedTarget &&
    (event.currentTarget as HTMLElement).contains(event.relatedTarget as Node)
  ) {
    return
  }
  isOpen.value = false
}
</script>

<style lang="scss" scoped>
.icon {
  color: $beige;
  transition: color $transition;

  .dark-mode & {
    color: $dark-brown;
  }
}

.main-button,
.element-button {
  @include focus-visible;
  display: grid;
  place-content: center;
  width: rem(44px);
  height: rem(44px);
  border-radius: 50%;
}

.main-button {
  background-color: $dark-brown;
  border: rem(1px) solid $dark-brown-20;
  transition: background-color $transition;

  &:focus-visible {
    border-radius: 50%;
  }

  .dark-mode & {
    background-color: $beige;
    border-color: $beige-20;
  }
}

.dropdown {
  position: relative;

  &--open {
    .icon {
      color: $dark-brown;

      .dark-mode & {
        color: $beige;
      }
    }

    .main-button {
      background-color: transparent;

      .dark-mode & {
        background-color: transparent;
      }
    }
  }
}

.backdrop {
  position: fixed;
  inset: 0;
  z-index: -1;
  background-color: rgba(black, 0.5);
}

.content {
  position: absolute;
  left: 50%;
  display: flex;
  flex-direction: column;
  gap: rem(12px);
  padding-top: 12px;
  transform: translateX(-50%);
}

.element-button {
  background-color: $beige;
  border: rem(1px) solid $dark-brown-20;
  box-shadow: $base-shadow;

  &:focus-visible {
    border-radius: 50%;
  }

  .dark-mode & {
    border-color: $beige;
  }

  .contrast-mode & {
    border-color: $dark-brown;
  }
}

:deep(.nested-icon) {
  --background-color: #{$beige} !important;
  color: $dark-brown !important;
}
</style>
